/**
 * Calendar helper utilities.
 *
 * Contains pure, reusable functions for date classification, formatting,
 * and API-response transformation used by the calendar feature.
 */

const SUNDAY = 0;
const SATURDAY = 6;

const CURRENCY_LOCALE = 'es-AR';
const FRACTION_DIGITS = 2;

const PAD_LENGTH = 2;
const PAD_CHAR = '0';

// ─── Date classification ────────────────────────────────────────────

/**
 * Returns true when the given date falls on Saturday or Sunday.
 * @param {Date} date
 * @returns {boolean}
 */
export const isWeekend = (date) => {
    const day = date.getDay();
    return day === SUNDAY || day === SATURDAY;
};

/**
 * Returns true when the given date is today (time-insensitive).
 * @param {Date} date
 * @returns {boolean}
 */
export const isToday = (date) => {
    const now = new Date();
    return (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
    );
};

/**
 * Returns true when the given date is strictly after today (time-insensitive).
 * Compares year/month/day components directly to avoid creating unnecessary
 * Date copies that could introduce subtle bugs.
 * @param {Date} date
 * @returns {boolean}
 */
export const isFutureDay = (date) => {
    const now = new Date();
    if (date.getFullYear() !== now.getFullYear()) return date.getFullYear() > now.getFullYear();
    if (date.getMonth() !== now.getMonth()) return date.getMonth() > now.getMonth();
    return date.getDate() > now.getDate();
};

// ─── Formatting ─────────────────────────────────────────────────────

/**
 * Formats a Date as an ISO-style key: 'yyyy-MM-dd'.
 * @param {Date} date
 * @returns {string}
 */
export const formatDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(PAD_LENGTH, PAD_CHAR);
    const d = String(date.getDate()).padStart(PAD_LENGTH, PAD_CHAR);
    return `${y}-${m}-${d}`;
};

/**
 * Formats a Date for user-facing display: 'dd/MM/yyyy'.
 * @param {Date} date
 * @returns {string}
 */
export const formatDateDisplay = (date) => {
    const d = String(date.getDate()).padStart(PAD_LENGTH, PAD_CHAR);
    const m = String(date.getMonth() + 1).padStart(PAD_LENGTH, PAD_CHAR);
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
};

/**
 * Formats a number as Argentine-locale currency string: '$ X.XXX,XX'.
 * @param {number|string} amount
 * @returns {string}
 */
export const formatCalendarCurrency = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return '$ 0,00';

    return `$ ${num.toLocaleString(CURRENCY_LOCALE, {
        minimumFractionDigits: FRACTION_DIGITS,
        maximumFractionDigits: FRACTION_DIGITS,
    })}`;
};

// ─── API params ─────────────────────────────────────────────────────

/**
 * Extracts month (zero-padded) and year from a Date, ready for the
 * payment-schedule API query params.
 * @param {Date} date
 * @returns {{ month: string, year: number }}
 */
export const getApiMonthYear = (date) => {
    const month = String(date.getMonth() + 1).padStart(PAD_LENGTH, PAD_CHAR);
    const year = date.getFullYear();
    return { month, year };
};

/**
 *
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
export const parseAmount = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    if (typeof value !== 'string') return 0;

    const trimmed = value.trim();
    if (trimmed === '') return 0;

    const hasComma = trimmed.includes(',');
    const dotCount = (trimmed.match(/\./g) || []).length;

    if (hasComma || dotCount > 1) {
        // Argentine/European locale: dots are thousands separators, comma is decimal
        const cleaned = trimmed.replace(/\./g, '').replace(',', '.');
        const result = parseFloat(cleaned);
        return isNaN(result) ? 0 : result;
    }

    const result = parseFloat(trimmed);
    return isNaN(result) ? 0 : result;
};

// ─── API transforms ─────────────────────────────────────────────────

/**
 * Transforms the payment-schedule API response (array of days) into the
 * object format the Calendar component expects:
 *   { 'yyyy-MM-dd': { amountToPay: number, amountPaid: number } }
 *
 * Weekend days are excluded so those calendar cells render empty.
 *
 * @param {Object} response - API response with a `days` array.
 * @param {Array}  response.days - Each entry has { date, amountToPay, amountPaid }.
 * @returns {Object} Date-keyed map of financial data.
 */
export const transformCalendarResponse = (response) => {
    const dataMap = {};

    if (!response?.days) return dataMap;

    response.days.forEach((day) => {
        const date = new Date(day.date + 'T00:00:00');

        if (!isWeekend(date)) {
            dataMap[day.date] = {
                amountToPay: parseAmount(day.amountToPay),
                amountPaid: parseAmount(day.amountPaid),
            };
        }
    });

    return dataMap;
};
